import React, { useEffect, useRef } from 'react';
import { Info, ExternalLink } from 'lucide-react';
import { useWebSocketStore } from '../../../../store/websocketStore';
import { useChatStore } from '../../store/chatStore';
import { useConversationStatusStore } from '../../store/conversationStatusStore';
import { useMemory } from '../../../../api/memory';
import { ConnectionStatus } from './ConnectionStatus';
import { PleriChatLanding } from './PleriChatLanding';
import { MessageList } from './MessageList';
import { ChatInput } from './ChatInput';
import { AskQuestionPanel } from '../MessageContent/AskQuestionPanel';
import { UsageValueBanner } from './UsageValueBanner';
import { TotalUsageDisplay } from '../ChatBox/TotalUsageDisplay';
import { useIsPleriDebugMode } from './MessageActions';
import { Disclaimer } from '../common/Disclaimer';
import '../../../../styles/scroll-anchoring.css';
import { pleriRouter } from '../../../../Pleri';

import { useTranslation } from 'react-i18next';

interface ChatAreaProps {
  isCompact?: boolean;
  onToolExpand?: () => void;
  /**
   * If true, the tool use block will not collapse when the tool is expanded.
   * This is useful for the chat area in the small chat window.
   */
  preventToolUseBlockToCollapse?: boolean;
  isReadOnly?: boolean;
  showMessageFromIndex?: number;
  /**
   * If true, the message list will automatically scroll to the bottom on updates.
   * Defaults to true.
   */
  autoScrollToBottom?: boolean;
}

export const ChatArea: React.FC<ChatAreaProps> = ({
  isCompact,
  onToolExpand,
  preventToolUseBlockToCollapse,
  isReadOnly = false,
  showMessageFromIndex = 0,
  autoScrollToBottom = true,
}) => {
  const wsStatus = useWebSocketStore((state) => state.status);
  const { conversationStatus, setConversationStatus, resetConversationStatus } =
    useConversationStatusStore();

  const activeConversationId = useChatStore((s) => s.activeConversationId);
  const loadingConversationId = useChatStore((s) => s.loadingConversationId);

  const activeConversation = useChatStore((s) =>
    s.activeConversationId ? s.conversations[s.activeConversationId] : null,
  );

  const hasMessages = Boolean(activeConversation?.messages?.length);
  const scope = activeConversation?.scope;
  const slackThreadUrl = activeConversation?.meta?.slackThreadUrl;
  const isConversationLoading = activeConversation?.isLoading;
  const activeAskQuestion = useChatStore((s) => s.activeAskQuestion);

  const { data: memoryResponse } = useMemory();
  const isUserMemoryEmpty =
    !memoryResponse?.memory?.communicationPreference ||
    !memoryResponse?.memory?.role;

  useEffect(() => {
    if (isConversationLoading && conversationStatus === 'idle') {
      setConversationStatus('loading');
    }
    if (!isConversationLoading && conversationStatus === 'loading') {
      setConversationStatus('completed');
    }
  }, [isConversationLoading, conversationStatus, setConversationStatus]);

  const prevActiveConversationIdRef = useRef(activeConversationId);

  useEffect(() => {
    if (prevActiveConversationIdRef.current !== activeConversationId) {
      prevActiveConversationIdRef.current = activeConversationId;
      resetConversationStatus();
    }
  }, [activeConversationId, resetConversationStatus]);

  const isPleriDebugMode = useIsPleriDebugMode();
  const shouldShowSlackInfo = slackThreadUrl && scope === 'team';

  // Info box for Slack team conversations
  const SlackThreadInfoBox = () => {
    const { t } = useTranslation('pleri/chat');
    if (!shouldShowSlackInfo || !slackThreadUrl) {
      return null;
    }

    const urlString =
      typeof slackThreadUrl === 'string'
        ? slackThreadUrl
        : String(slackThreadUrl);

    const handleOpenSlack = () => {
      window.open(urlString, '_blank', 'noopener,noreferrer');
    };

    return (
      <div className="border-t border-gray-100 bg-white px-4 py-3 dark:border-gray-800 dark:bg-gray-900">
        <div className="mx-auto max-w-3xl">
          <div
            onClick={handleOpenSlack}
            className="group relative cursor-pointer rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-4 shadow-sm transition-all duration-200 hover:border-blue-300 hover:shadow-md active:scale-[0.98] dark:border-blue-700 dark:from-blue-950 dark:to-gray-900"
          >
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-600 transition-colors group-hover:bg-blue-200 dark:bg-blue-900">
                <Info className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {t('continueThisConversationInSlack')}
                  </h3>
                  <ExternalLink className="h-3.5 w-3.5 flex-shrink-0 text-gray-400 transition-colors group-hover:text-blue-600 dark:text-gray-500" />
                </div>
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                  {t('thisIsAGroupConversationHappeningIn')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Simple loading component
  const ConversationLoadingSkeleton = () => {
    const { t } = useTranslation('pleri/chat');

    return (
      <div className="flex h-full flex-1 items-center justify-center bg-white dark:bg-gray-900">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-[#FF732E] dark:border-gray-700"></div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('loadingConversation')}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="relative flex h-full w-full flex-col overflow-hidden bg-white dark:bg-gray-900">
      <div className="flex max-w-full flex-1 flex-col overflow-hidden">
        {loadingConversationId &&
        (loadingConversationId !== activeConversationId || !hasMessages) ? (
          // Show loading skeleton when switching to a conversation that's being loaded
          <ConversationLoadingSkeleton />
        ) : !activeConversationId || !hasMessages ? (
          <div className="flex-1 overflow-y-auto">
            <PleriChatLanding isCompact={isCompact} />
          </div>
        ) : (
          <>
            <ConnectionStatus status={wsStatus} />

            <div
              id={`chat-scrollable-container-${activeConversationId}`}
              key={activeConversationId}
              className="custom-scrollbar relative flex-1 overflow-y-auto bg-white p-4 dark:bg-gray-900"
              data-testid="pleri-chat-area"
            >
              <div className="mx-auto max-w-3xl">
                <MessageList
                  showMessageFromIndex={showMessageFromIndex}
                  isReadOnly={isReadOnly}
                  onToolExpand={onToolExpand}
                  preventToolUseBlockToCollapse={preventToolUseBlockToCollapse}
                  autoScrollToBottom={autoScrollToBottom}
                  showDownIndicator={autoScrollToBottom}
                  scrollableContainerId={`chat-scrollable-container-${activeConversationId}`}
                />
              </div>
            </div>

            {isPleriDebugMode && <TotalUsageDisplay />}

            {!isReadOnly && <UsageValueBanner />}

            {!isReadOnly &&
              (shouldShowSlackInfo ? (
                <SlackThreadInfoBox />
              ) : (
                <>
                  {isUserMemoryEmpty && conversationStatus === 'completed' && (
                    <div className="invisible flex justify-center bg-white dark:bg-gray-900">
                      <div className="invisible w-full max-w-[750px] px-2">
                        <div id="chameleon-banner-slot-pleri-memory" />
                        <div
                          id="chameleon-banner-slot-pleri-memory-button"
                          onClick={() => {
                            pleriRouter.navigate({
                              to: '/settings/memory',
                            });
                          }}
                          className="invisible"
                        />
                      </div>
                    </div>
                  )}

                  {activeAskQuestion ? (
                    <AskQuestionPanel isPreparing={Boolean(isConversationLoading)} />
                  ) : (
                    <ChatInput dropUpMenu={true} isCompact={isCompact} />
                  )}
                </>
              ))}
          </>
        )}
      </div>
      {/* Disclaimer at the bottom of the page */}
      <div className="w-full flex-shrink-0 px-4 pb-1">
        <div className="mx-auto max-w-3xl">
          <Disclaimer />
        </div>
      </div>
    </div>
  );
};
